import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box, Flex, HStack, Text, Button, Kbd, Image, useDisclosure, Drawer, DrawerOverlay,
  DrawerContent, IconButton, VStack, Link,
} from '@chakra-ui/react';
import { useAuth } from '../auth/AuthContext';
import { useDocs } from '../docs/DocsContext';
import SearchPalette from './SearchPalette';
import LayWidget from './LayWidget';

// 목록이 왜 여기서 끊기는지 알려 준다. 로그인 버튼은 헤더에 상시 있으므로 여기 두지 않는다.
function LockedNote() {
  return (
    <Box mx={2.5} p={3.5} borderWidth="1px" borderColor="neutral.200" borderRadius="8px" bg="neutral.50">
      <Text fontSize="12.5px" fontWeight="600" color="neutral.700" mb={1.5}>
        여기까지가 로그인 없이 보는 문서입니다
      </Text>
      <Text fontSize="12.5px" color="neutral.600" lineHeight="1.6">
        서비스 이용 방법을 담은 문서는 <b>회원가입 승인 후 로그인</b>하시면 열립니다.
      </Text>
    </Box>
  );
}

// 대상 스위치 — 관리자·겸직처럼 두 대상 문서를 함께 받는 사람에게만 보인다(2026-09-07).
// 크리에이터와 영상권리사 문서가 한 목록에 섞여 나오던 것을 좁혀 볼 수 있게 한다.
function AudienceSwitch() {
  const { canSwitchAudience, viewAudience, setViewAudience } = useDocs();
  if (!canSwitchAudience) return null;
  const options = [
    { key: null, label: '전체' },
    { key: 'creator', label: '크리에이터' },
    { key: 'rights', label: '영상권리사' },
  ];
  return (
    <Box px={2.5} pb={1}>
      <Text fontSize="10.5px" fontWeight="600" letterSpacing="0.11em" textTransform="uppercase"
        color="neutral.500" mb={1.5}>
        보기 대상
      </Text>
      <HStack spacing="1px" bg="neutral.100" p="2px" borderRadius="7px">
        {options.map((o) => {
          const active = viewAudience === o.key;
          return (
            <Button key={o.label} onClick={() => setViewAudience(o.key)} flex="1" h="26px" minW={0} px={1.5}
              borderRadius="5px" fontSize="11.5px" fontWeight={active ? '700' : '500'}
              bg={active ? 'white' : 'transparent'} color={active ? 'brand.700' : 'neutral.600'}
              boxShadow={active ? 'sm' : 'none'} _hover={{ bg: active ? 'white' : 'neutral.200' }}>
              {o.label}
            </Button>
          );
        })}
      </HStack>
    </Box>
  );
}

function NavTree({ onNavigate }) {
  const { signedIn } = useAuth();
  const { sections } = useDocs();
  return (
    <VStack align="stretch" spacing={6} px={3} py={6}>
      <AudienceSwitch />
      {sections.map((section) => (
        <React.Fragment key={section.label || '_'}>
          {/* 전체를 볼 때만 대상 머리말이 붙는다 — 자기 대상만 보는 사람에게는 종전 화면 그대로 */}
          {section.label && (
            <Text fontSize="11.5px" fontWeight="700" color="neutral.800" px={2.5} mt={1} mb={-3}
              borderTopWidth="1px" borderTopColor="neutral.200" pt={4}>
              {section.label}
            </Text>
          )}
          {section.groups.map((group) => (
            <Box key={`${section.label || ''}${group.group}`}>
              <Text fontSize="10.5px" fontWeight="600" letterSpacing="0.11em" textTransform="uppercase"
                color="neutral.500" px={2.5} mb={2}>
                {group.group}
              </Text>
              <VStack align="stretch" spacing="1px">
                {group.items.map((item) => (
                  <Link key={item.slug} as={NavLink} to={`/${item.slug}`} onClick={onNavigate}
                    px={2.5} py={1.5} borderRadius="6px" fontSize="13.5px" lineHeight="1.45"
                    color="neutral.600" borderLeftWidth="2px" borderLeftColor="transparent"
                    _hover={{ bg: 'neutral.100', color: 'neutral.800', textDecoration: 'none' }}
                    _activeLink={{ bg: 'brand.50', color: 'brand.700', fontWeight: '600', borderLeftColor: 'brand.500' }}>
                    {item.title}
                  </Link>
                ))}
              </VStack>
            </Box>
          ))}
        </React.Fragment>
      ))}
      {!signedIn && <LockedNote />}
    </VStack>
  );
}

export default function Layout({ children }) {
  const { profile, signOut, isPreview, signedIn } = useAuth();
  const search = useDisclosure();
  const drawer = useDisclosure();
  const location = useLocation();
  const [mac, setMac] = useState(false);

  useEffect(() => { setMac(/Mac|iPhone|iPad/.test(navigator.platform || '')); }, []);
  useEffect(() => { drawer.onClose(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); search.onOpen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [search]);

  return (
    <>
      <Flex as="header" position="sticky" top={0} zIndex={20} h="60px" align="center" gap={3} px={{ base: 3.5, md: 5 }}
        bg="whiteAlpha.900" backdropFilter="blur(12px)" borderBottomWidth="1px" borderColor="neutral.200">
        <IconButton aria-label="메뉴" variant="ghost" size="sm" display={{ base: 'flex', lg: 'none' }}
          onClick={drawer.onOpen} icon={<Text fontSize="18px">☰</Text>} />

        <HStack as={RouterLink} to="/" spacing={2.5} _hover={{ textDecoration: 'none' }} flexShrink={0}>
          <Image src="/laeebly-icon.png" alt="레이블리" w="24px" h="24px" flexShrink={0} />
          <Text fontSize="14.5px" fontWeight="600" letterSpacing="-0.02em" whiteSpace="nowrap">레이블리 도움말</Text>
          <Text display={{ base: 'none', md: 'block' }} fontSize="11px" color="neutral.400"
            borderLeftWidth="1px" borderColor="neutral.300" pl={2.5}>docs.laeebly.io</Text>
        </HStack>

        <Box flex="1" />

        <Button size="sm" variant="outline" onClick={search.onOpen} borderColor="neutral.300"
          color="neutral.500" fontWeight="400" fontSize="13px" pl={3} pr={2}
          _hover={{ borderColor: 'brand.300', color: 'neutral.700' }}>
          <Text display={{ base: 'none', sm: 'block' }} mr={6}>문서 검색</Text>
          <Text display={{ base: 'block', sm: 'none' }}>⌕</Text>
          <Kbd display={{ base: 'none', sm: 'block' }} fontSize="10px">{mac ? '⌘' : 'Ctrl'}K</Kbd>
        </Button>

        <HStack spacing={2} pl={1}>
          {signedIn ? (
            <>
              <Text display={{ base: 'none', md: 'block' }} fontSize="12px" color="neutral.500"
                maxW="180px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {isPreview ? `미리보기 · ${profile?.previewLabel}` : profile?.email}
              </Text>
              <Button size="sm" variant="ghost" fontSize="12.5px" color="neutral.500"
                _hover={{ color: 'neutral.700', bg: 'neutral.100' }} onClick={signOut}>
                {isPreview ? '역할 전환' : '로그아웃'}
              </Button>
            </>
          ) : (
            <Button as={RouterLink} to={`/login?next=${encodeURIComponent(location.pathname)}`}
              size="sm" bg="brand.500" color="white" fontSize="12.5px"
              _hover={{ bg: 'brand.600', textDecoration: 'none' }}>
              로그인
            </Button>
          )}
        </HStack>
      </Flex>

      <Flex align="flex-start">
        <Box as="nav" aria-label="문서 목록" display={{ base: 'none', lg: 'block' }} w="252px" flexShrink={0}
          position="sticky" top="60px" h="calc(100dvh - 60px)" overflowY="auto"
          borderRightWidth="1px" borderColor="neutral.200">
          <NavTree />
        </Box>

        <Box as="main" flex="1" minW={0} px={{ base: 5, md: 10 }} pt={9}>
          {children}
        </Box>
      </Flex>

      <Drawer isOpen={drawer.isOpen} placement="left" onClose={drawer.onClose}>
        <DrawerOverlay />
        <DrawerContent maxW="278px" bg="white">
          {/* DrawerContent 는 flex column 이다. 목록이 화면보다 길면 여기서 스크롤되어야 한다 —
              flex/minH 없이는 내용이 넘쳐도 잘린 채 스크롤되지 않는다. */}
          <Box pt={3} flex="1" minH={0} overflowY="auto" overscrollBehavior="contain">
            <NavTree onNavigate={drawer.onClose} />
          </Box>
        </DrawerContent>
      </Drawer>

      <SearchPalette isOpen={search.isOpen} onClose={search.onClose} />
      {/* 레이 챗봇 — REACT_APP_LAY_WIDGET=1 일 때만 렌더. /login 은 Layout 밖이라 자동으로 숨는다. */}
      <LayWidget />
    </>
  );
}
